import { Badge, Menu, Tooltip, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import type { MilestoneStatus } from '../../types/milestones';
import { formatTimeInMilestoneStatus } from '../../utils/milestoneStatusDuration';

const statusConfig: Record<MilestoneStatus, { label: string; color: string }> = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' },
};

function normalizeMilestoneStatus(status: string | null | undefined): MilestoneStatus {
  if (status && status in statusConfig) {
    return status as MilestoneStatus;
  }
  return 'not_started';
}

export function InteractiveMilestoneStatusBadge({
  status,
  statusUpdatedAt,
  onStatusChange,
}: {
  status: MilestoneStatus | string;
  statusUpdatedAt?: string | null;
  onStatusChange: (status: MilestoneStatus) => void;
}) {
  const theme = useMantineTheme();
  const [menuOpened, setMenuOpened] = useState(false);

  const normalizedStatus = normalizeMilestoneStatus(status);
  const config = statusConfig[normalizedStatus];
  const timeInStatusLabel =
    normalizedStatus === 'not_started'
      ? null
      : formatTimeInMilestoneStatus(statusUpdatedAt, config.label);

  const tooltipStyles = {
    tooltip: {
      backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
      border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
      borderRadius: 10,
      color: theme.colorScheme === 'dark' ? '#fff' : '#000',
      padding: '10px 12px',
      maxWidth: 320,
      fontSize: 13,
      lineHeight: 1.35,
      whiteSpace: 'normal' as const,
      wordBreak: 'break-word' as const,
      // Prevent tooltip from stealing hover and closing itself.
      pointerEvents: 'none' as const,
    },
    arrow: {
      border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    },
  };

  return (
    <Menu
      shadow="md"
      width={160}
      opened={menuOpened}
      onChange={setMenuOpened}
      withinPortal
    >
      <Tooltip
        label={timeInStatusLabel ?? ''}
        disabled={!timeInStatusLabel || menuOpened}
        openDelay={150}
        withArrow
        withinPortal
        styles={tooltipStyles}
      >
        <Menu.Target>
          <UnstyledButton
            aria-label={`Status: ${config.label}`}
            sx={{
              display: 'inline-flex',
              borderRadius: 1000,
              lineHeight: 1,
              '&:focus-visible': {
                outline: `2px solid ${theme.colors.blue[5]}`,
                outlineOffset: 2,
              },
            }}
          >
            <Badge
              color={config.color}
              variant="filled"
              size="sm"
              style={{ cursor: 'pointer', padding: '8px 12px', pointerEvents: 'none' }}
              rightSection={
                <IconChevronDown
                  size={12}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                />
              }
            >
              {config.label}
            </Badge>
          </UnstyledButton>
        </Menu.Target>
      </Tooltip>
      <Menu.Dropdown>
        {(Object.entries(statusConfig) as [MilestoneStatus, { label: string; color: string }][]).map(
          ([value, statusInfo]) => (
            <Menu.Item
              key={value}
              onClick={() => onStatusChange(value)}
              style={{
                padding: '2px 4px',
                margin: '2px 4px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Badge
                color={statusInfo.color}
                variant="filled"
                size="sm"
                style={{
                  padding: '8px 12px',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                {statusInfo.label}
              </Badge>
            </Menu.Item>
          )
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
